/**
 * formulario de criacao de pesquisa.
 * objeto + N itens (add/remove inline). validacao via zod do shared-types.
 * no success, redireciona pra /pesquisas/:id.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  type ItemPesquisaInput,
  type PesquisaCreate,
  PesquisaCreateSchema,
} from '@licita-mind/shared-types';
import { Logo } from '../components/Logo';
import { useCreatePesquisa } from '../hooks/usePesquisas';
import { ApiError } from '../lib/api';

const itemVazio = (): ItemPesquisaInput => ({
  descricao: '',
  unidade: 'UN',
  quantidade: 1,
  catmat: null,
  catser: null,
  especificacao_tecnica: null,
});

export const PesquisaNovaPage = () => {
  const nav = useNavigate();
  const [objeto, setObjeto] = useState('');
  const [itens, setItens] = useState<ItemPesquisaInput[]>([itemVazio()]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const mutation = useCreatePesquisa();

  const updateItem = (idx: number, patch: Partial<ItemPesquisaInput>): void => {
    setItens((cur) =>
      cur.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    );
  };
  const removeItem = (idx: number): void => {
    setItens((cur) => (cur.length === 1 ? cur : cur.filter((_, i) => i !== idx)));
  };
  const addItem = (): void => setItens((cur) => [...cur, itemVazio()]);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setErrors({});

    // sanitiza catmat/catser: '' -> null
    const limpos: ItemPesquisaInput[] = itens.map((it) => ({
      ...it,
      catmat: it.catmat?.trim() ? it.catmat.trim() : null,
      catser: it.catser?.trim() ? it.catser.trim() : null,
      especificacao_tecnica: it.especificacao_tecnica?.trim()
        ? it.especificacao_tecnica.trim()
        : null,
    }));

    const candidate: PesquisaCreate = { objeto, itens: limpos };
    const parsed = PesquisaCreateSchema.safeParse(candidate);
    if (!parsed.success) {
      const flat: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        flat[issue.path.join('.')] = issue.message;
      }
      setErrors(flat);
      return;
    }
    mutation.mutate(parsed.data, {
      onSuccess: (created) => {
        nav(`/pesquisas/${created.id}`);
      },
    });
  };

  const apiErr = mutation.error instanceof ApiError ? mutation.error : null;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white border-b border-navy-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" aria-label="voltar">
            <Logo />
          </Link>
          <Link to="/" className="btn-ghost text-sm">
            ← voltar
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-8">
        <h2 className="text-2xl font-display font-semibold mb-1">nova pesquisa de preços</h2>
        <p className="text-sm text-navy-500 mb-6">
          defina o objeto da contratação e os itens. você pode adicionar mais itens depois.
        </p>

        <form onSubmit={onSubmit} className="card space-y-6">
          {apiErr && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
              <strong>{apiErr.code}:</strong> {apiErr.message}
            </div>
          )}

          <div>
            <label className="label" htmlFor="objeto">
              objeto da contratação
            </label>
            <input
              id="objeto"
              className="input"
              placeholder="ex: aquisição de material de expediente para 12 meses"
              value={objeto}
              onChange={(e) => setObjeto(e.target.value)}
              required
              maxLength={500}
            />
            {errors.objeto && <p className="error">{errors.objeto}</p>}
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-semibold text-sm uppercase tracking-wider text-gold-600">
                itens ({itens.length})
              </h3>
              <button type="button" onClick={addItem} className="btn-secondary text-sm">
                + adicionar item
              </button>
            </div>

            <div className="space-y-4">
              {itens.map((item, idx) => (
                <fieldset
                  key={idx}
                  className="border border-navy-200 rounded-md p-4 space-y-3 bg-navy-50/30"
                >
                  <div className="flex items-center justify-between">
                    <legend className="text-xs font-medium text-navy-500 uppercase">
                      item {idx + 1}
                    </legend>
                    {itens.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(idx)}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        remover
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="label">descrição</label>
                    <input
                      className="input"
                      placeholder="ex: Papel A4 75g/m² — resma com 500 folhas"
                      value={item.descricao}
                      onChange={(e) => updateItem(idx, { descricao: e.target.value })}
                      required
                    />
                    {errors[`itens.${idx}.descricao`] && (
                      <p className="error">{errors[`itens.${idx}.descricao`]}</p>
                    )}
                  </div>

                  <div>
                    <label className="label">especificação técnica (opcional)</label>
                    <textarea
                      className="input"
                      rows={2}
                      placeholder="detalhes técnicos: gramatura, alvura, marca aceita..."
                      value={item.especificacao_tecnica ?? ''}
                      onChange={(e) =>
                        updateItem(idx, { especificacao_tecnica: e.target.value })
                      }
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="label">unidade</label>
                      <input
                        className="input"
                        placeholder="UN / RM / KG"
                        value={item.unidade}
                        onChange={(e) => updateItem(idx, { unidade: e.target.value })}
                        required
                        maxLength={20}
                      />
                    </div>
                    <div>
                      <label className="label">quantidade</label>
                      <input
                        type="number"
                        className="input"
                        min="0.0001"
                        step="any"
                        value={item.quantidade}
                        onChange={(e) =>
                          updateItem(idx, { quantidade: Number(e.target.value) })
                        }
                        required
                      />
                    </div>
                    <div>
                      <label className="label">CATMAT (opcional)</label>
                      <input
                        className="input font-mono"
                        placeholder="150000"
                        value={item.catmat ?? ''}
                        onChange={(e) => updateItem(idx, { catmat: e.target.value })}
                        pattern="[0-9]{6,12}"
                        title="6 a 12 dígitos"
                      />
                    </div>
                  </div>
                  {errors[`itens.${idx}.catser`] && (
                    <p className="error">{errors[`itens.${idx}.catser`]}</p>
                  )}
                </fieldset>
              ))}
            </div>

            {errors.itens && <p className="error mt-2">{errors.itens}</p>}
          </div>

          <div className="flex justify-end pt-2 border-t border-navy-100">
            <button type="submit" className="btn-primary" disabled={mutation.isPending}>
              {mutation.isPending ? 'criando...' : 'criar pesquisa'}
            </button>
          </div>
        </form>
        <p className="text-center text-xs text-navy-500 mt-6">by lions intelligence</p>
      </main>
    </div>
  );
};
